import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Sparkles } from 'lucide-react';
import { schedule as fallbackSchedule } from '../mock';
import { useSchedule } from '../hooks/useSchedule';

const statusMap = {
  on: { label: 'Live', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  maybe: { label: 'Misschien', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  off: { label: 'Niet live', color: 'bg-zinc-700/40 text-zinc-400 border-zinc-700/60' }
};

const MONTHS_NL = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Returns Monday-date of the week currently shown on the homepage.
// Rule: schedule rolls over to "next week" every Monday at 00:00 local time.
function getActiveWeekMonday(now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // JS getDay: 0=Sunday, 1=Monday, ... 6=Saturday
  const dow = now.getDay();
  // Days back to Monday of current week (Mon=0, Tue=1, ... Sun=6)
  const daysSinceMonday = (dow + 6) % 7;
  d.setDate(d.getDate() - daysSinceMonday);
  return d;
}

function formatDayDate(date) {
  return `${date.getDate()} ${MONTHS_NL[date.getMonth()]}`;
}

function isSameDate(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function Schedule() {
  const { items } = useSchedule();
  const schedule = items.length ? items : fallbackSchedule;

  // Tick every minute so the week auto-advances Sunday 18:00 without refresh.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const weekStart = getActiveWeekMonday(now);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const weekRangeLabel = `${formatDayDate(weekStart)} – ${formatDayDate(weekEnd)}`;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return (
    <section id="schedule" className="relative py-24 px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-violet-300 text-sm font-semibold mb-2">
              <Calendar className="w-4 h-4" /> WEEKSCHEMA
              <span
                data-testid="schedule-week-range"
                className="ml-2 px-2 py-0.5 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-200 text-[11px] font-bold tracking-wider"
              >
                {weekRangeLabel}
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight">
              Wanneer ben ik live?
            </h2>
            <p className="text-zinc-400 mt-3 max-w-xl">
              Streams meestal in de avond. Schema kan altijd schuiven — check
              Discord voor laatste updates.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Sparkles className="w-4 h-4 text-violet-300" /> Tijden in CET
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {schedule.map((d, i) => {
            const st = statusMap[d.status];
            const active = d.status === 'on';
            const dayDate = new Date(weekStart);
            dayDate.setDate(weekStart.getDate() + i);
            const isToday = isSameDate(dayDate, today);
            return (
              <div
                key={d.day}
                data-testid={`schedule-day-${i}`}
                className={`relative rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${
                  active
                    ? 'bg-gradient-to-b from-violet-600/20 to-violet-600/5 border-violet-500/30'
                    : 'bg-white/[0.03] border-white/10 hover:border-white/20'
                }`}
                style={
                  isToday
                    ? { boxShadow: '0 0 0 2px #a855f7, 0 0 18px #a855f755' }
                    : undefined
                }
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="text-xs uppercase tracking-wider text-zinc-400"
                    data-testid={`schedule-day-${i}-date`}
                  >
                    {formatDayDate(dayDate)}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.color}`}
                  >
                    {st.label}
                  </span>
                </div>
                <div className="text-white font-bold text-lg">{d.day}</div>
                {(d.game || d.time || active) ? (
                  <>
                    {d.game && (
                      <div className="text-sm text-violet-300 mt-2 font-medium leading-snug break-words">
                        {d.game}
                      </div>
                    )}
                    {d.time && (
                      <div className="flex items-center gap-1 text-xs text-zinc-400 mt-2">
                        <Clock className="w-3 h-3" /> {d.time}
                      </div>
                    )}
                    {!d.game && !d.time && (
                      <div className="text-sm text-zinc-500 mt-2">— Tijd volgt</div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-zinc-500 mt-2">— Geen stream</div>
                )}
                {isToday && (
                  <span
                    className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-black tracking-wider text-zinc-900 bg-violet-500 shadow-lg"
                    data-testid={`schedule-day-${i}-today-badge`}
                  >
                    VANDAAG
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
